/**
 * Installs a demo shift + attendance policy for testing auto PR/LOP.
 * Data is stored in the same localStorage keys as AttendancePolicyComponent.
 */
import {
  POLICIES_STORAGE_KEY,
  SHIFTS_STORAGE_KEY,
  loadAttendancePolicies,
  loadAttendanceShifts,
} from "./attendancePolicyEngine";

export const DEMO_SHIFT_ID = "general";
export const DEMO_POLICY_ID = "demo-auto-attendance-v1";

const DEMO_SHIFT = {
  id: DEMO_SHIFT_ID,
  name: "General (9 AM – 6 PM)",
  startTime: "09:00",
  endTime: "18:00",
  breakMinutes: 60,
  paidBreakMinutes: 0,
  unpaidBreakMinutes: 60,
  totalBreakMinutes: 60,
  isDefault: true,
};

/** Matches AttendancePolicyComponent default policy shape (active, all employees). */
const DEMO_POLICY = {
  id: DEMO_POLICY_ID,
  policyName: "Demo Auto Attendance (Test)",
  status: "active",
  isDefault: true,
  allEmployees: true,
  effectiveDate: "2020-01-01",
  shiftGroupId: DEMO_SHIFT_ID,
  graceCheckInMinutes: 15,
  graceCheckOutMinutes: 15,
  considerLateAfterMinutes: 15,
  considerEarlyBeforeMinutes: 30,
  fullDayHours: 8,
  halfDayHours: 4,
  fullDayIfHoursAtLeast: 8,
  halfDayIfHoursLessThan: 4,
  halfDayMarkIfHoursBelow: 4,
  absentMarkIfHoursBelow: 2,
  autoAbsentMarkingEnabled: true,
  autoHalfDayMarkingEnabled: true,
  defaultMarkingForMissingPunch: "absent",
  missingPunchHandling: "auto_mark_default",
  punchMode: "double",
  checkInOutRule: "first_in_last_out",
  workHoursCalcMethod: "first_in_last_out",
  breakDeductionMethod: "fixed",
  breakFixedMinutes: 60,
  graceRulesEnabled: true,
  lateAllowedOccurrencesPerMonth: 3,
  earlyExitAllowedOccurrencesPerMonth: 3,
  latePenaltyAction: "none",
  earlyExitPenaltyAction: "none",
  autoLogoutEnabled: false,
  checkInMobile: true,
  checkInWeb: true,
};

export const ATTENDANCE_POLICY_UPDATED_EVENT = "hrms-attendance-policy-updated";

export function dispatchPolicyUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(ATTENDANCE_POLICY_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

/**
 * Merge demo shift/policy into localStorage if missing.
 * @returns {{ installed: boolean, policyId: string, shiftId: string }}
 */
export function ensureDemoAttendancePolicy() {
  let installed = false;
  const shifts = loadAttendanceShifts();
  if (!shifts.some((s) => String(s.id) === DEMO_SHIFT_ID)) {
    localStorage.setItem(SHIFTS_STORAGE_KEY, JSON.stringify([DEMO_SHIFT, ...shifts]));
    installed = true;
  }

  const policies = loadAttendancePolicies();
  if (!policies.some((p) => String(p.id) === DEMO_POLICY_ID)) {
    localStorage.setItem(
      POLICIES_STORAGE_KEY,
      JSON.stringify([{ ...DEMO_POLICY, updatedAt: new Date().toISOString() }, ...policies])
    );
    installed = true;
  }

  if (installed) dispatchPolicyUpdated();
  return { installed, policyId: DEMO_POLICY_ID, shiftId: DEMO_SHIFT_ID };
}

export function getDemoPolicyTestGuide() {
  return {
    policyName: DEMO_POLICY.policyName,
    shift: "09:00 – 18:00 (60 min break)",
    graceLate: "15 min after 09:00",
    presentIf: "Net hours ≥ 8 after check-out",
    lopIf: "No punch, missing check-out, hours < 8, or late beyond grace+15 min",
    examplePresent: "In 09:10, Out 18:05 → PR",
    exampleLop: "In 11:00, Out 15:00 → LOP",
    todayBehavior:
      "Before check-in: -. After check-in until 8h+ net: LOP. After check-out: PR or LOP by hours.",
    futureDates: "Always -",
    pastNoPunch: "Working days without punch/API row: - (not LOP until backend marks absent)",
  };
}
